import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext'; // Import useAuth
import { TextField, Button, Box, Typography, MenuItem, Select, InputLabel, FormControl, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PessoaFotoCapture from './PessoaFotoCapture';
import FormCard from './FormCard';
import MaskedInput from './MaskedInput';

function PessoaForm({ onPessoaAdded, initialData, onCancelEdit }) {
  const { user } = useAuth(); // Get user from context
  const [id, setId] = useState(null);
  const [empresa_id, setEmpresaId] = useState('');
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [rg, setRg] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [nascimento, setNascimento] = useState('');
  const [sexo, setSexo] = useState('');
  const [telefone1, setTelefone1] = useState('');
  const [telefone2, setTelefone2] = useState('');
      const [email, setEmail] = useState('');
      const [is_whatsapp_enabled, setIsWhatsappEnabled] = useState(false); // New state
      const [empresas, setEmpresas] = useState([]);
  const [error, setError] = useState('');
  const [fotoCaptureOpen, setFotoCaptureOpen] = useState(false);

  useEffect(() => {
    // Carregar lista de empresas para o select apenas se o usuario for admin
    if (user && user.tipo_usuario === 'admin') {
      api.get('/empresas')
        .then(response => {
          setEmpresas(response.data.data || response.data || []);
        })
        .catch(error => {
          console.error('Erro ao buscar empresas:', error);
        });
    }

    if (initialData) {
      setId(initialData.id);
      setEmpresaId(initialData.empresa_id);
      setNome(initialData.nome);
      setCpf(initialData.cpf || '');
      setRg(initialData.rg || '');
      setCnpj(initialData.cnpj || '');
      setNascimento(initialData.nascimento ? initialData.nascimento.split('T')[0] : ''); // Formato YYYY-MM-DD
      setSexo(initialData.sexo || '');
      setTelefone1(initialData.telefone1 || '');
      setTelefone2(initialData.telefone2 || '');
      setEmail(initialData.email || '');
      setIsWhatsappEnabled(initialData.is_whatsapp_enabled || false); // Initialize new field
    } else {
      clearForm();
    }
  }, [initialData, user]); // Adicionar user como dependencia

  const clearForm = () => {
    setId(null);
    setEmpresaId('');
    setNome('');
    setCpf('');
    setRg('');
    setCnpj('');
    setNascimento('');
    setSexo('');
    setTelefone1('');
    setTelefone2('');
    setEmail('');
    setIsWhatsappEnabled(false); // Reset new field
    setError(''); // Reset error message
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const pessoaData = {
      empresa_id: user.tipo_usuario === 'admin' ? empresa_id : user.empresa_id, // Define empresa_id com base no tipo de usuario
      empresa_id_original: id ? empresa_id : undefined,
      nome,
      cpf: cpf || null,
      rg: rg || null,
      cnpj: cnpj || null,
      nascimento: nascimento || null,
      sexo: sexo || null,
      telefone1: telefone1 || null,
      telefone2: telefone2 || null,
      email: email || null,
      is_whatsapp_enabled, // Include new field
    };

    if (id) {
      // Modo de edicao
      api.put(`/pessoas/${id}`, pessoaData)
        .then(response => {
          console.log('Pessoa atualizada:', response.data);
          clearForm();
          onPessoaAdded();
          onCancelEdit();
        })
        .catch(error => {
          if (error.response && error.response.status === 409) {
            setError(error.response.data.message || 'Registro duplicado.');
          } else {
            console.error('Erro ao atualizar pessoa:', error);
          }
        });
    } else {
      // Modo de criacao
      api.post('/pessoas', pessoaData)
        .then(response => {
          console.log('Pessoa adicionada:', response.data);
          const newPessoaId = response.data.id; // Get the ID of the newly created person
          if (newPessoaId) {
            // Fetch the complete new person object
            api.get(`/pessoas?id=${newPessoaId}`)
              .then(personResponse => {
                const newPessoa = (personResponse.data.data || personResponse.data)[0]; // Assuming API returns array
                if (newPessoa) {
                  clearForm();
                  onPessoaAdded(newPessoa); // Pass the new person object to the callback
                } else {
                  console.error('Erro: Pessoa rec\u00E9m-adicionada n\u00E3o encontrada.');
                  clearForm();
                  onPessoaAdded(); // Call without data if not found
                }
              })
              .catch(error => {
                console.error('Erro ao buscar pessoa recém-adicionada:', error);
                clearForm();
                onPessoaAdded(); // Call without data on error
              });
          } else {
            console.error('Erro: ID da pessoa rec\u00E9m-adicionada n\u00E3o retornado.');
            clearForm();
            onPessoaAdded(); // Call without data if ID not returned
          }
        })
        .catch(error => {
          if (error.response && error.response.status === 409) {
            setError(error.response.data.message || 'Registro duplicado.');
          } else {
            console.error('Erro ao adicionar pessoa:', error);
          }
        });
    }
  };

  return (
    <>
      <FormCard
        component="form"
        onSubmit={handleSubmit}
        sx={{
          mt: 2,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          pb: { xs: 8, sm: 9 },
        }}
      >
        <Typography variant="h5" gutterBottom>
          {id ? 'Editar Pessoa' : 'Adicionar Nova Pessoa'}
        </Typography>
        {(user && user.tipo_usuario === 'admin') && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="empresa-select-label">Empresa</InputLabel>
            <Select
              labelId="empresa-select-label"
              value={empresa_id}
              label="Empresa"
              onChange={(e) => setEmpresaId(e.target.value)}
            >
              {empresas.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          label="Nome Completo"
          variant="outlined"
          fullWidth
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          margin="normal"
          required
        />
        <TextField
          label="CPF"
          variant="outlined"
          fullWidth
          value={cpf}
          onChange={(e) => setCpf(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }} // Added shrink prop
          InputProps={{
            inputComponent: MaskedInput,
            inputProps: {
              mask: "000.000.000-00",
              definitions: {
                '#': /[0-9]/,
              },
              unmask: true,
            },
          }}
        />
        <TextField
          label="RG"
          variant="outlined"
          fullWidth
          value={rg}
          onChange={(e) => setRg(e.target.value)}
          margin="normal"
        />
        <TextField
          label="CNPJ"
          variant="outlined"
          fullWidth
          value={cnpj}
          onChange={(e) => setCnpj(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }} // Added shrink prop
          InputProps={{
            inputComponent: MaskedInput,
            inputProps: {
              mask: "00.000.000/0000-00",
              definitions: {
                '#': /[0-9]/,
              },
              unmask: true,
            },
          }}
        />
        <TextField
          label="Data de Nascimento"
          variant="outlined"
          fullWidth
          type="date"
          value={nascimento}
          onChange={(e) => setNascimento(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="sexo-select-label">Sexo</InputLabel>
          <Select
            labelId="sexo-select-label"
            value={sexo}
            label="Sexo"
            onChange={(e) => setSexo(e.target.value)}
          >
            <MenuItem value=""><em>Nenhum</em></MenuItem>
            <MenuItem value="M">Masculino</MenuItem>
            <MenuItem value="F">Feminino</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Telefone 1"
          variant="outlined"
          fullWidth
          value={telefone1}
          onChange={(e) => setTelefone1(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }} // Added shrink prop
          InputProps={{
            inputComponent: MaskedInput,
            inputProps: {
              mask: [
                { mask: '(00) 0000-0000' },
                { mask: '(00) 00000-0000' }
              ],
              lazy: false,
              placeholderChar: '_',
              unmask: true, // Ensure onAccept provides the unmasked value
            },
          }}
        />
        <TextField
          label="Telefone 2"
          variant="outlined"
          fullWidth
          value={telefone2}
          onChange={(e) => setTelefone2(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }} // Added shrink prop
          InputProps={{
            inputComponent: MaskedInput,
            inputProps: {
              mask: [
                { mask: '(00) 0000-0000' },
                { mask: '(00) 00000-0000' }
              ],
              lazy: false,
              placeholderChar: '_',
              unmask: true, // Ensure onAccept provides the unmasked value
            },
          }}
        />
        <TextField
          label="Email"
          variant="outlined"
          fullWidth
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={is_whatsapp_enabled}
              onChange={(e) => setIsWhatsappEnabled(e.target.checked)}
            />
          }
          label="Habilitado para WhatsApp?"
          sx={{ mt: 1, mb: 1 }}
        />
        <Box
          sx={{ mt: 2, mb: 1 }}
        >
          {id && (
            <Button
              variant="outlined"
              startIcon={<CameraAltIcon />}
              onClick={() => setFotoCaptureOpen(true)}
              fullWidth
            >
              Capturar foto para reconhecimento
            </Button>
          )}
        </Box>
        <Box
          sx={{
            position: 'sticky',
            bottom: 'max(16px, env(safe-area-inset-bottom, 0px) + 8px)',
            display: 'flex',
            gap: 1.5,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            mt: 2,
            pt: 2,
            pb: 1,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            zIndex: 1,
          }}
        >
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={onCancelEdit}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
          >
            {id ? 'Atualizar Pessoa' : 'Adicionar Pessoa'}
          </Button>
        </Box>
      </FormCard>

      {id && (
        <PessoaFotoCapture
          open={fotoCaptureOpen}
          onClose={() => setFotoCaptureOpen(false)}
          pessoaId={id}
          empresaId={user.tipo_usuario === 'admin' ? empresa_id : user.empresa_id}
        />
      )}

      <Dialog open={!!error} onClose={() => setError('')} maxWidth="xs" fullWidth>
        <DialogTitle>Aviso</DialogTitle>
        <DialogContent>
          <Typography>{error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setError('')} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PessoaForm;
